/**
 * File Step Executors — Read/write from IndexedDB + server file storage
 *
 * Config for file_read:
 * - source: 'local' | 'server'
 * - fileId: string
 *
 * Config for file_write:
 * - target: 'local' | 'server'
 * - fileName: string
 * - content: string | Blob
 * - mimeType?: string
 * - metadata?: Record<string, unknown>
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';
import { fileStorageService } from '../../../services/fileStorageService';
import { serverFileService } from '../../../services/serverFileService';

export const fileStepExecutors: StepExecutor[] = [
  {
    type: 'file_read',
    async execute(step: EngineStep): Promise<StepExecutionResult> {
      try {
        const source = (step.config.source as string) || 'local';
        const fileId = step.config.fileId as string;

        if (!fileId) {
          return { success: false, error: 'fileId is required for file_read' };
        }

        if (source === 'server') {
          const url = serverFileService.getFileUrl(fileId);
          return {
            success: true,
            output: { fileUrl: url, fileId, source: 'server' },
          };
        }

        // Local (IndexedDB)
        const file = await fileStorageService.getFile(fileId);
        if (!file) {
          return { success: false, error: `File not found: ${fileId}` };
        }

        return {
          success: true,
          output: {
            file: {
              id: file.id,
              name: file.name,
              type: file.type,
              size: file.size,
              metadata: file.metadata,
            },
            fileId,
            source: 'local',
          },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'File read failed',
        };
      }
    },
  },
  {
    type: 'file_write',
    async execute(step: EngineStep): Promise<StepExecutionResult> {
      try {
        const target = (step.config.target as string) || 'local';
        const fileName = step.config.fileName as string;
        const content = step.config.content as string;
        const mimeType = (step.config.mimeType as string) || 'text/plain';
        const metadata = (step.config.metadata as Record<string, unknown>) || {};

        if (!fileName) {
          return { success: false, error: 'fileName is required for file_write' };
        }

        if (target === 'server') {
          const blob = new Blob([content], { type: mimeType });
          const file = new File([blob], fileName, { type: mimeType });
          const result = await serverFileService.uploadFile(file);
          return {
            success: true,
            output: { fileId: result.id, fileUrl: result.url, target: 'server' },
          };
        }

        // Local (IndexedDB)
        const blob = new Blob([content], { type: mimeType });
        const result = await fileStorageService.saveFile(blob, fileName, metadata);
        return {
          success: true,
          output: { fileId: result.id, target: 'local' },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'File write failed',
        };
      }
    },
  },
];
